import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { pdApi, type PdFramework } from '../api';
import './FrameworkDesigner.css';
import { HelpPanel } from '../HelpPanel';
import { FRAMEWORK_DESIGNER } from '../helpContent';

/**
 * Settings → Assessment Framework → Framework Designer.
 *
 * CLONED from `/Users/glucic/src/ai_readiness_template/views/setup-framework-designer.ejs`.
 * A BOARD, not a list: one slate column per dimension, one white card per component.
 *
 * WHY A BOARD. The layout carries the data. A component's column says which dimension it
 * belongs to, and its position within that column is its display order — so arranging the
 * board IS editing the framework, which is why the screen has a Save Layout action and a
 * list does not.
 *
 * A SEPARATE SCREEN FROM THE BUILDER, deliberately. The Builder edits questions and answers
 * inside components; this arranges which dimensions and components the framework selects
 * at all. They were briefly one component behind a flag here, which was wrong — the source
 * ships two screens and they do different jobs.
 *
 * `hasBeenScored` FREEZES THE BOARD, and it is broader than the `is_locked` flag: any
 * opportunity carrying a score or a scored date against this framework is enough. Moving a
 * component to another dimension after that would silently re-attribute scores that have
 * already been reported.
 *
 * NOT PORTED FROM THE SOURCE: drag-and-drop reordering, which the source does with
 * SortableJS. The cards, columns and Save Layout action are all here and reordering is the
 * one thing that needs a drag library this platform does not carry. Logged in
 * docs/DEVIATIONS.md rather than substituted with a different interaction.
 */

type Dict = Record<string, unknown>;

export function FrameworkDesigner() {
  const location = useLocation();
  const [frameworks, setFrameworks] = useState<PdFramework[]>([]);
  const [frameworkId, setFrameworkId] = useState('');
  const [board, setBoard] = useState<Dict | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picker, setPicker] = useState<'dimension' | 'component' | null>(null);
  const [pickerDimension, setPickerDimension] = useState<number | null>(null);

  const requested = new URLSearchParams(location.search).get('framework');

  useEffect(() => {
    void (async () => {
      try {
        const data = await pdApi.settings.frameworks();
        setFrameworks(data.frameworks);
        // The source does NOT auto-select — it opens with "No Framework Selected" unless a
        // framework arrives on the query string. Reproduced.
        if (requested && data.frameworks.some((f) => f.framework_id === requested)) {
          setFrameworkId(requested);
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [requested]);

  const load = useCallback(async () => {
    if (!frameworkId) {
      setBoard(null);
      return;
    }
    setError(null);
    try {
      setBoard((await pdApi.settings.designer(frameworkId)) as unknown as Dict);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [frameworkId]);

  useEffect(() => {
    void load();
  }, [load]);

  const frozen = Boolean(board?.hasBeenScored);
  const dimensions = (board?.dimensions as Dict[]) ?? [];
  const unassignedDimensions = (board?.unassignedDimensions as Dict[]) ?? [];
  const unassignedComponents = (board?.unassignedComponents as Dict[]) ?? [];

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setPicker(null);
      setPickerDimension(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    /*
      `ir-fd` SCOPES THIS SCREEN'S STYLES. APR's Framework Designer uses the SAME `fd-`
      class names — ten of them collide, including `.fd-card`, `.fd-card-name` and
      `.fd-weight-badge` — and CSS is global once bundled, so APR's rules were reaching this
      screen and truncating every component name with an ellipsis. Scoping the rules that
      matter under this class settles it without touching APR.
    */
    <div className="fd-container ir-fd">
      <div className="fd-header">
        <h1 className="fd-header__title">Framework Designer</h1>
        <p className="fd-header__subtitle">
          Arrange the dimensions and components this framework selects.
        </p>

        <div className="fd-header-row">
          <select
            id="frameworkSelector"
            className="fd-selector"
            value={frameworkId}
            onChange={(e) => setFrameworkId(e.target.value)}
          >
            <option value="">Select a framework…</option>
            {frameworks.map((f) => (
              <option key={f.framework_id} value={f.framework_id}>
                {f.framework_name}
                {f.is_default ? ' ★' : ''}
                {f.is_locked ? ' 🔒' : ''}
              </option>
            ))}
          </select>

          <button
            type="button"
            className={`fd-action-btn fd-btn-add-dimension${frozen || !frameworkId ? ' fd-disabled' : ''}`}
            disabled={frozen || !frameworkId || busy}
            title={
              frozen
                ? 'Cannot add dimensions to a locked framework'
                : 'Add a dimension to this framework'
            }
            onClick={() => setPicker('dimension')}
          >
            + Add Dimension
          </button>

          <button
            type="button"
            className={`fd-action-btn fd-btn-add-component${frozen || !frameworkId ? ' fd-disabled' : ''}`}
            disabled={frozen || !frameworkId || busy}
            title={
              frozen
                ? 'Cannot add components to a locked framework'
                : 'Add a component to this framework'
            }
            onClick={() => {
              setPickerDimension((dimensions[0]?.id as number) ?? null);
              setPicker('component');
            }}
          >
            + Add Component
          </button>

          <button
            type="button"
            className={`fd-action-btn fd-btn-save-layout${frozen || !frameworkId ? ' fd-disabled' : ''}`}
            disabled={frozen || !frameworkId || busy}
            title={
              frozen ? 'Cannot modify layout of a locked framework' : 'Save layout changes'
            }
            onClick={() => void load()}
          >
            Save Layout
          </button>
        </div>
      </div>
      <div className="pd-page-header__actions">
        <HelpPanel help={FRAMEWORK_DESIGNER} />
      </div>

      {error ? <div className="fd-error">{error}</div> : null}

      <div className="fd-board" id="boardContainer">
        {loading ? (
          <div className="fd-empty-board">Loading…</div>
        ) : !frameworkId ? (
          <div className="fd-empty-board">
            <div className="fd-empty-icon">🖼️</div>
            <h5>No Framework Selected</h5>
            <p>Please select a framework from the dropdown above to start designing</p>
          </div>
        ) : dimensions.length === 0 ? (
          <div className="fd-empty-board">
            <div className="fd-empty-icon">🖼️</div>
            <h5>No dimensions yet</h5>
            <p>Add a dimension to start designing this framework</p>
          </div>
        ) : (
          dimensions.map((dim) => {
            const components = (dim.components as Dict[]) ?? [];
            return (
              <div key={String(dim.id)} className="fd-column">
                <div className="fd-column-header">
                  <div>
                    <h5 className="fd-column-title">{String(dim.name)}</h5>
                    <span className="fd-component-count">{components.length} components</span>
                  </div>
                  <div className="fd-column-actions">
                    {frozen ? (
                      <span className="fd-locked-chip" title="Scored — layout frozen">
                        🔒
                      </span>
                    ) : (
                      <button
                        type="button"
                        title="Remove this dimension from the framework"
                        disabled={busy}
                        onClick={() => {
                          if (
                            !window.confirm(
                              `Remove "${String(dim.name)}" from this framework?`,
                            )
                          )
                            return;
                          return run(() =>
                            pdApi.settings.removeDimension(frameworkId, Number(dim.id)),
                          );
                        }}
                      >
                        ⋮
                      </button>
                    )}
                  </div>
                </div>

                <div className="fd-components-list">
                  {components.map((c) => {
                    const weight = Number(c.weightage ?? 3);
                    return (
                      <div
                        key={String(c.id)}
                        className={`fd-card ${frozen ? 'fd-card--fixed' : 'fd-card--movable'}`}
                      >
                        <div className="fd-card-header">
                          <span className="fd-card-name">{String(c.name)}</span>
                          {/* Weight only. The question count was noise on a screen about
                              arranging dimensions and components (owner, 2026-08-10). */}
                          <span className={`fd-weight-badge fd-weight-${weight}`}>{weight}</span>
                        </div>

                        {c.description ? (
                          <p className="fd-card-description">{String(c.description)}</p>
                        ) : null}

                        <div className="fd-card-actions">
                          <button
                            type="button"
                            title="Remove this component from the framework"
                            disabled={frozen || busy}
                            onClick={() => {
                              if (
                                !window.confirm(
                                  `Remove "${String(c.name)}" from this framework?`,
                                )
                              )
                                return;
                              return run(() =>
                                pdApi.settings.removeComponent(frameworkId, Number(c.id)),
                              );
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {components.length === 0 ? (
                    <div style={{ color: '#cbd5e1', fontSize: 12, padding: '8px 2px' }}>
                      No components in this dimension.
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ---- add a dimension from the shared pool ---- */}
      {picker === 'dimension' ? (
        <div className="fd-modal-backdrop" onClick={() => setPicker(null)}>
          <div className="fd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fd-modal-head">
              <h2>Add Dimension</h2>
              <button type="button" className="fd-modal-close" onClick={() => setPicker(null)}>
                ×
              </button>
            </div>
            <div className="fd-modal-body">
              <div className="fd-pick-list">
                {unassignedDimensions.length === 0 ? (
                  <p style={{ color: '#64748b', fontSize: 13 }}>
                    Every dimension in the shared pool is already on this framework. Create a
                    new one below.
                  </p>
                ) : (
                  unassignedDimensions.map((d) => (
                    <div key={String(d.id)} className="fd-pick">
                      <span>
                        <strong>{String(d.name)}</strong>{' '}
                        <span style={{ color: '#94a3b8' }}>{String(d.dimension_code ?? '')}</span>
                      </span>
                      <button
                        type="button"
                        className="fd-pick-add"
                        disabled={busy}
                        onClick={() =>
                          run(() =>
                            pdApi.settings.addDimension(frameworkId, {
                              name: d.name,
                              dimension_code: d.dimension_code,
                              category: d.category,
                            }),
                          )
                        }
                      >
                        Add
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="fd-modal-foot">
              <button type="button" className="fd-modal-cancel" onClick={() => setPicker(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="fd-modal-save"
                disabled={busy}
                onClick={() => run(() => pdApi.settings.addDimension(frameworkId))}
              >
                New Dimension
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ---- add a component to a chosen dimension ---- */}
      {picker === 'component' ? (
        <div className="fd-modal-backdrop" onClick={() => setPicker(null)}>
          <div className="fd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fd-modal-head">
              <h2>Add Component</h2>
              <button type="button" className="fd-modal-close" onClick={() => setPicker(null)}>
                ×
              </button>
            </div>
            <div className="fd-modal-body">
              <div style={{ marginBottom: 16 }}>
                <label
                  htmlFor="fd-pick-dim"
                  style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}
                >
                  Dimension
                </label>
                <select
                  id="fd-pick-dim"
                  className="fd-selector"
                  style={{ width: '100%' }}
                  value={pickerDimension ?? ''}
                  onChange={(e) => setPickerDimension(Number(e.target.value))}
                >
                  {dimensions.map((d) => (
                    <option key={String(d.id)} value={String(d.id)}>
                      {String(d.name)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fd-pick-list">
                {unassignedComponents.length === 0 ? (
                  <p style={{ color: '#64748b', fontSize: 13 }}>
                    Every component in the shared pool is already on this framework. Create a
                    new one below.
                  </p>
                ) : (
                  unassignedComponents.map((c) => (
                    <div key={String(c.id)} className="fd-pick">
                      <span>
                        <strong>{String(c.name)}</strong>{' '}
                        <span style={{ color: '#94a3b8' }}>{String(c.component_code ?? '')}</span>
                      </span>
                      <button
                        type="button"
                        className="fd-pick-add"
                        disabled={busy || !pickerDimension}
                        onClick={() =>
                          run(() =>
                            pdApi.settings.addComponent(frameworkId, {
                              dimension_id: pickerDimension,
                              name: c.name,
                              component_code: c.component_code,
                              weightage: c.weightage,
                            }),
                          )
                        }
                      >
                        Add
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="fd-modal-foot">
              <button type="button" className="fd-modal-cancel" onClick={() => setPicker(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="fd-modal-save"
                disabled={busy || !pickerDimension}
                onClick={() =>
                  run(() =>
                    pdApi.settings.addComponent(frameworkId, { dimension_id: pickerDimension }),
                  )
                }
              >
                New Component
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
